import { FC, useEffect, useState } from 'react'

import { AppSelect, AppSelectOptionProps } from '../../../atoms/AppSelect/AppSelect'
import SuccessModal from '../../SuccessModal'
import TalentRankMessage from './TalentRankMessage'
import RetentionAtRiskMessage from './RetentionAtRiskMessage'
import { TalentRankSummary } from '../../../../types/definitions/talentRank'
import { isTalentRankTitle } from '../../../../utils/talentRankTitle'

export interface DropdownQuestionProps {
  title: string
  value: string
  placeholder: string
  description?: string
  options: { label: string; value: string }[]
  unfinished?: boolean
  required?: boolean
  onChange?: (value: string) => void
  previousAnswer?: string
  suggested?: boolean
  suggestedOptionValue?: string
  talentRankSummary?: TalentRankSummary
}

export const DropdownQuestion: FC<DropdownQuestionProps> = ({
  title,
  value,
  options,
  placeholder,
  description,
  onChange,
  unfinished,
  required = true,
  previousAnswer,
  suggested = false,
  suggestedOptionValue,
  talentRankSummary,
}) => {
  const [selectValue, setSelectValue] = useState(value)
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false)

  useEffect(() => {
    setSelectValue(value)
  }, [value])

  const handleSelectedOption = (selectedOption: string): void => {
    onChange?.(selectedOption)
    setSelectValue(selectedOption)
  }

  const questionOptionsSortFn = (): void => {
    return
  }

  const isTalentRankQuestion = isTalentRankTitle(title)
  const talentRankSelectLabel = 'Override Talent Category (Optional)'
  const summaryScore =
    typeof talentRankSummary?.averageScore === 'number'
      ? Math.max(0, Math.min(100, Math.round(talentRankSummary.averageScore)))
      : undefined
  const summaryCategory = talentRankSummary?.categoryLabel ?? suggestedOptionValue
  const scoreThresholds = talentRankSummary?.thresholds || []

  const renderTalentRankSummary = (): JSX.Element => (
    <>
      <div className="rounded-xl bg-orange-soft px-6 py-5 text-black text-2xl leading-8 font-medium">
        <p className="font-medium">
          The assessment result (Score) is{' '}
          <span className="font-bold text-orange">{summaryScore !== undefined ? summaryScore : '--'} out of 100</span>,
          which automatically places this employee in the category:{' '}
        </p>
        <p className="font-medium mt-4">
          <span className="font-bold text-3xl text-orange">{summaryCategory ?? '--'}</span>.
        </p>
        <p className="font-medium mt-4">
          This category is calculated from the average of the individual scores obtained in each assessment category
          (Growth Mindset, Communication, Professional Skills, Productivity, Commitment, and AI).
        </p>
        {scoreThresholds.length > 0 && (
          <div className="mt-4 text-xl font-normal leading-7">
            <p className="font-bold text-orange">Ranking criteria:</p>
            <ul className="mt-2 list-disc list-inside space-y-1">
              {scoreThresholds.map(threshold => (
                <li key={`${threshold.range}-${threshold.value}`}>
                  {threshold.range} → {threshold.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="flex items-center">
        <span className="app-select-label">{talentRankSelectLabel}</span>
      </div>
      <div className="rounded-xl bg-orange-soft px-6 py-5 text-black text-2xl leading-8 font-medium">
        <p className="font-medium">
          The system has assigned <span className="font-semibold text-3xl text-orange">{summaryCategory ?? '--'}</span>.
          You may change this category if you consider another category more accurate.
        </p>
      </div>
    </>
  )

  const moreInformationMessage = (): JSX.Element | undefined => {
    // TODO: Correct this hardcoded string
    if (isTalentRankQuestion) {
      return (
        <div className="space-y-4 -mb-2">
          <div className="flex items-center">
            <span className="app-select-label">Talent Category</span>
            {!required && <p className="optional-question ml-auto">Optional</p>}
          </div>
          {renderTalentRankSummary()}
        </div>
      )
    }

    switch (title) {
      case 'Retention at Risk':
        return (
          <div className="flex justify-between -mb-2">
            <span className="app-select-label">{title}</span>
            <div className="flex items-center">
              <button
                onClick={() => setShowInfoModal(true)}
                className="p-2 rounded-md text-orange font-extrabold mx-2 underline decoration-1"
              >
                More information
              </button>
            </div>
            {!required && <p className="optional-question ml-auto">Optional</p>}
          </div>
        )
      default:
        return undefined
    }
  }

  const messageInSuccessModal = (): JSX.Element | undefined => {
    if (isTalentRankQuestion) {
      return <TalentRankMessage />
    }

    switch (title) {
      case 'Retention at Risk':
        return <RetentionAtRiskMessage />
      default:
        return undefined
    }
  }

  const processOptions = (options: { label: string; value: string }[]): AppSelectOptionProps[] => {
    const newOptions: AppSelectOptionProps[] = []
    options.forEach(option => {
      const _extraLabels: { label: string; color?: string }[] = []
      // only show the previous answer extra label when the previous answer coincides
      // with this option AND when there is no answer yet. Once there is an answer, do not show
      // the previous label
      const isPreviousAnswer = previousAnswer === option.value && value === ''
      const isSuggestedOption = suggested && suggestedOptionValue !== undefined && option.value === suggestedOptionValue
      if (isPreviousAnswer) _extraLabels.push({ label: 'Previous' })
      if (isSuggestedOption) _extraLabels.push({ label: 'Suggested', color: 'text-[#F2994A]' })
      newOptions.push({
        label: option.label,
        value: option.value,
        extraLabels: _extraLabels,
      })
    })
    return newOptions
  }

  return (
    <div className="dropdown-question">
      <div className={`dropdown-question__content${isTalentRankQuestion ? ' pb-10' : ''}`}>
        <AppSelect
          value={selectValue}
          label={isTalentRankQuestion ? talentRankSelectLabel : title}
          placeholder={placeholder}
          options={processOptions(options)}
          unfinished={unfinished}
          required={required}
          customSortFn={questionOptionsSortFn}
          onChange={handleSelectedOption}
          className="text-dark-blue h-28"
          labelBody={moreInformationMessage()}
        />
      </div>
      <SuccessModal
        visible={showInfoModal}
        icon={false}
        onClose={() => {
          setShowInfoModal(false)
        }}
        message={messageInSuccessModal()}
        buttonText="Close"
      />
    </div>
  )
}
